import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Row, Col, Card, Button, Checkbox, Input, Tag, Typography, Space,
  Drawer, List, Tooltip, Badge, message, Spin, Empty, Divider,
  Popconfirm, Select, Alert, Modal, Form, Upload, Table, Tabs,
} from 'antd';
import {
  FileTextOutlined, RobotOutlined, CopyOutlined, DownloadOutlined,
  HistoryOutlined, DeleteOutlined, CheckSquareOutlined, BorderOutlined,
  SyncOutlined, EditOutlined, SaveOutlined, CloseOutlined, LinkOutlined,
  UploadOutlined, ApiOutlined, GlobalOutlined, CheckCircleOutlined,
  FileMarkdownOutlined, FilePdfOutlined, FileOutlined,
} from '@ant-design/icons';
import { devSpecAPI } from '../services/api';

const { TabPane } = Tabs;
const API_BASE = '/api/v1';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}` });

// ── API Doc Library Component ─────────────────────────────────────────────────
function ApiDocLibrary() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState({});

  const reload = async () => {
    const res = await fetch(`${API_BASE}/api-docs`, { headers: authHeader() });
    const data = await res.json();
    setDocs(Array.isArray(data) ? data : []);
  };

  useEffect(() => { reload(); }, []);

  const handleUpload = async ({ file }) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api-docs`, {
        method: 'POST',
        headers: authHeader(),
        body: form,
      });
      const data = await res.json();
      if (data.ok) {
        message.success(`已上傳：${data.filename}`);
        reload();
      } else {
        message.error(data.detail || '上傳失敗');
      }
    } finally {
      setUploading(false);
    }
    return false;
  };

  const copyLink = (doc, type) => {
    const base = window.location.origin;
    const url = type === 'raw'
      ? `${base}${API_BASE}/api-docs/${doc.id}/raw`
      : `${base}${API_BASE}/api-docs/${doc.id}`;
    navigator.clipboard.writeText(url);
    setCopied(prev => ({ ...prev, [`${doc.id}_${type}`]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [`${doc.id}_${type}`]: false })), 2000);
    message.success('連結已複製！可直接貼給 Claude Code 使用');
  };

  const copyClaudePrompt = (doc) => {
    const base = window.location.origin;
    const prompt = `請閱讀以下 API 文件並根據其規格進行開發：\n${base}${API_BASE}/api-docs/${doc.id}/raw`;
    navigator.clipboard.writeText(prompt);
    message.success('Claude Code 提示已複製');
  };

  const handleDelete = async (id) => {
    await fetch(`${API_BASE}/api-docs/${id}`, { method: 'DELETE', headers: authHeader() });
    reload();
  };

  const extIcon = (ext) => {
    if (ext === '.md') return <FileMarkdownOutlined style={{ color: '#722ed1' }} />;
    if (ext === '.pdf') return <FilePdfOutlined style={{ color: '#f5222d' }} />;
    if (ext === '.json' || ext === '.yaml' || ext === '.yml') return <ApiOutlined style={{ color: '#1890ff' }} />;
    return <FileOutlined style={{ color: '#595959' }} />;
  };

  const cols = [
    {
      title: '檔案',
      render: (_, r) => (
        <Space>
          {extIcon(r.ext)}
          <div>
            <Text strong style={{ fontSize: 13 }}>{r.filename}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {(r.size / 1024).toFixed(1)} KB · 上傳於 {new Date(r.uploaded_at).toLocaleString('zh-TW')}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: '分享連結', width: 320,
      render: (_, r) => {
        const base = window.location.origin;
        const rawUrl = `${base}${API_BASE}/api-docs/${r.id}/raw`;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Text code style={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                {rawUrl}
              </Text>
              <Button size="small" type={copied[`${r.id}_raw`] ? 'primary' : 'default'}
                icon={copied[`${r.id}_raw`] ? <CheckCircleOutlined /> : <CopyOutlined />}
                onClick={() => copyLink(r, 'raw')}>
                {copied[`${r.id}_raw`] ? '已複製' : '複製連結'}
              </Button>
            </div>
            <Button size="small" icon={<RobotOutlined />} style={{ background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}
              onClick={() => copyClaudePrompt(r)}>
              複製 Claude Code 提示
            </Button>
          </div>
        );
      },
    },
    {
      title: '', width: 50,
      render: (_, r) => (
        <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(r.id)} okText="刪除" cancelText="取消" okButtonProps={{ danger: true }}>
          <Button danger type="text" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Alert
        message={
          <span>
            <ApiOutlined /> 上傳 API 文件後，系統會產生一個<strong>可公開存取的連結</strong>。
            管理者可複製此連結給其他平台的 <Text code>Claude Code</Text> 使用，讓 AI 直接閱讀 API 規格進行開發。
          </span>
        }
        type="info" showIcon={false} style={{ marginBottom: 16 }}
      />

      <Upload.Dragger
        beforeUpload={() => false}
        customRequest={handleUpload}
        showUploadList={false}
        accept=".md,.txt,.json,.yaml,.yml,.pdf,.html"
        style={{ marginBottom: 16 }}
      >
        <p className="ant-upload-drag-icon">
          {uploading ? <Spin /> : <UploadOutlined style={{ fontSize: 28, color: '#1890ff' }} />}
        </p>
        <p style={{ fontSize: 14, fontWeight: 500 }}>拖放或點擊上傳 API 文件</p>
        <p style={{ color: '#999', fontSize: 12 }}>支援：Markdown (.md)、PDF、JSON、YAML、TXT · 最大 10MB</p>
      </Upload.Dragger>

      {docs.length === 0 ? (
        <Empty description="尚未上傳任何 API 文件" style={{ padding: '20px 0' }} />
      ) : (
        <Table dataSource={docs} columns={cols} rowKey="id" size="small" pagination={false} />
      )}

      <Divider />
      <div style={{ background: '#f6f0ff', padding: 12, borderRadius: 8, border: '1px solid #d3adf7' }}>
        <Text strong style={{ color: '#531dab' }}>如何讓 Claude Code 使用此 API 文件？</Text>
        <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, color: '#555' }}>
          <li>上傳 API 文件（Markdown 最佳）</li>
          <li>點擊「複製 Claude Code 提示」</li>
          <li>在另一個平台的 Claude Code 對話中貼上提示</li>
          <li>Claude Code 會自動讀取並按照 API 規格開發</li>
        </ol>
      </div>
    </div>
  );
}

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CATEGORY_COLORS = {
  '行銷核心': 'blue',
  'AI 功能': 'purple',
  'AI 業務競賽': 'gold',
  'AI SEO / AEO': 'cyan',
  '廣告整合': 'orange',
  '排程自動化': 'green',
  '合作夥伴': 'magenta',
  '意向書 / 招標': 'red',
  '系統管理': 'geekblue',
  '垂直產業': 'volcano',
};

function SpecMarkdown({ content }) {
  if (!content) return null;
  // Simple markdown renderer: headers, code blocks, bold, bullets
  const lines = content.split('\n');
  const elements = [];
  let inCode = false;
  let codeLines = [];
  let codeLang = '';

  lines.forEach((line, i) => {
    if (line.startsWith('```')) {
      if (!inCode) {
        inCode = true;
        codeLang = line.slice(3).trim();
        codeLines = [];
      } else {
        elements.push(
          <pre key={i} style={{
            background: '#1e1e1e', color: '#d4d4d4', borderRadius: 6,
            padding: '12px 16px', fontSize: 12, overflowX: 'auto',
            marginBottom: 12, lineHeight: 1.6,
          }}>
            {codeLang && <div style={{ color: '#858585', fontSize: 10, marginBottom: 6 }}>{codeLang}</div>}
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        inCode = false;
        codeLines = [];
        codeLang = '';
      }
      return;
    }
    if (inCode) { codeLines.push(line); return; }

    if (line.startsWith('# ')) {
      elements.push(<Title key={i} level={2} style={{ marginTop: 24, marginBottom: 8, color: '#1a1a2e' }}>{line.slice(2)}</Title>);
    } else if (line.startsWith('## ')) {
      elements.push(<Title key={i} level={3} style={{ marginTop: 20, marginBottom: 6, color: '#003a8c', borderBottom: '2px solid #e8f4ff', paddingBottom: 4 }}>{line.slice(3)}</Title>);
    } else if (line.startsWith('### ')) {
      elements.push(<Title key={i} level={4} style={{ marginTop: 16, marginBottom: 4, color: '#0050b3' }}>{line.slice(4)}</Title>);
    } else if (line.startsWith('#### ')) {
      elements.push(<Title key={i} level={5} style={{ marginTop: 12, marginBottom: 4 }}>{line.slice(5)}</Title>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const text = renderInline(line.slice(2));
      elements.push(<div key={i} style={{ paddingLeft: 20, marginBottom: 4, lineHeight: 1.6 }}>• {text}</div>);
    } else if (/^\d+\. /.test(line)) {
      const match = line.match(/^(\d+)\. (.*)/);
      if (match) {
        const text = renderInline(match[2]);
        elements.push(<div key={i} style={{ paddingLeft: 20, marginBottom: 4 }}>{match[1]}. {text}</div>);
      }
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} style={{ borderLeft: '4px solid #1890ff', paddingLeft: 12, margin: '8px 0', color: '#595959', fontStyle: 'italic' }}>
          {renderInline(line.slice(2))}
        </blockquote>
      );
    } else if (line.startsWith('---') || line.startsWith('===')) {
      elements.push(<Divider key={i} />);
    } else if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: 8 }} />);
    } else {
      elements.push(<Paragraph key={i} style={{ marginBottom: 6, lineHeight: 1.7 }}>{renderInline(line)}</Paragraph>);
    }
  });

  return <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{elements}</div>;
}

function renderInline(text) {
  // Bold **text**, inline code `code`, links
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);

    const matches = [
      boldMatch && { type: 'bold', match: boldMatch },
      codeMatch && { type: 'code', match: codeMatch },
      linkMatch && { type: 'link', match: linkMatch },
    ].filter(Boolean).sort((a, b) => a.match.index - b.match.index);

    if (matches.length === 0) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    const first = matches[0];
    if (first.match.index > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, first.match.index)}</span>);
    }

    if (first.type === 'bold') {
      parts.push(<strong key={key++}>{first.match[1]}</strong>);
    } else if (first.type === 'code') {
      parts.push(
        <code key={key++} style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 3, fontSize: 12, fontFamily: 'monospace', color: '#c7254e' }}>
          {first.match[1]}
        </code>
      );
    } else if (first.type === 'link') {
      parts.push(<a key={key++} href={first.match[2]} target="_blank" rel="noreferrer">{first.match[1]}</a>);
    }

    remaining = remaining.slice(first.match.index + first.match[0].length);
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

export default function DevSpecPage() {
  const { specId } = useParams();
  const [catalog, setCatalog] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentSpec, setCurrentSpec] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [title, setTitle] = useState('開發規格');
  const [note, setNote] = useState('');
  const [platformCtx, setPlatformCtx] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editNote, setEditNote] = useState('');
  const [ctxModalOpen, setCtxModalOpen] = useState(false);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await devSpecAPI.getCatalog();
      if (res.data?.catalog) setCatalog(res.data.catalog);
    } catch (e) {
      message.error('載入功能清單失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await devSpecAPI.list();
      if (res.data?.specs) setHistoryList(res.data.specs);
    } catch (e) {
      message.error('載入歷史紀錄失敗');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Auto-load spec when URL contains :specId
  useEffect(() => {
    if (!specId) return;
    devSpecAPI.get(specId).then(res => {
      if (res.data) {
        setCurrentSpec(res.data);
        setTitle(res.data.title);
        setNote(res.data.note || '');
        setSelected(res.data.selected_features || []);
      }
    }).catch(() => message.error('找不到此規格版本'));
  }, [specId]);

  const handleCopyLink = (token) => {
    if (!token) { message.warning('此版本尚無分享連結，請重新整理'); return; }
    const url = `${window.location.origin}/spec/${token}`;
    navigator.clipboard.writeText(url);
    message.success('閱讀連結已複製（無需登入）');
  };

  const allFeatureKeys = Object.values(catalog).flat().map(f => f.key);
  const allSelected = allFeatureKeys.length > 0 && selected.length === allFeatureKeys.length;
  const someSelected = selected.length > 0 && selected.length < allFeatureKeys.length;

  const toggleAll = () => {
    if (allSelected) setSelected([]);
    else setSelected([...allFeatureKeys]);
  };

  const toggleCategory = (categoryFeatures) => {
    const keys = categoryFeatures.map(f => f.key);
    const allCatSelected = keys.every(k => selected.includes(k));
    if (allCatSelected) {
      setSelected(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelected(prev => [...new Set([...prev, ...keys])]);
    }
  };

  const toggleFeature = (key) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleGenerate = async () => {
    if (selected.length === 0) { message.warning('請至少選擇一個功能模組'); return; }
    setGenerating(true);
    try {
      const res = await devSpecAPI.generate({
        selected_features: selected,
        title,
        note,
        platform_context: platformCtx,
      });
      if (res.data) {
        setCurrentSpec(res.data);
        message.success(`規格已生成！版本 v${res.data.version_num}`);
      }
    } catch (e) {
      message.error('生成失敗：' + (e.message || '未知錯誤'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!currentSpec?.spec_content) return;
    navigator.clipboard.writeText(currentSpec.spec_content);
    message.success('已複製到剪貼板');
  };

  const handleDownload = () => {
    if (!currentSpec?.spec_content) return;
    const blob = new Blob([currentSpec.spec_content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dev-spec-v${currentSpec.version_num}-${currentSpec.title.replace(/[^a-zA-Z0-9一-龥]/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('已下載 Markdown 檔案');
  };

  const openHistory = () => {
    setHistoryOpen(true);
    loadHistory();
  };

  const loadFromHistory = async (id) => {
    try {
      const res = await devSpecAPI.get(id);
      if (res.data) {
        setCurrentSpec(res.data);
        setTitle(res.data.title);
        setNote(res.data.note || '');
        setSelected(res.data.selected_features || []);
        setHistoryOpen(false);
        message.success(`已載入版本 v${res.data.version_num}`);
      }
    } catch (e) {
      message.error('載入失敗');
    }
  };

  const deleteFromHistory = async (id, e) => {
    e?.stopPropagation();
    try {
      await devSpecAPI.delete(id);
      message.success('已刪除');
      loadHistory();
      if (currentSpec?.id === id) setCurrentSpec(null);
    } catch (e) {
      message.error('刪除失敗');
    }
  };

  const saveTitle = async () => {
    if (!currentSpec) return;
    try {
      await devSpecAPI.update(currentSpec.id, { title: editTitle, note: editNote });
      setCurrentSpec(prev => ({ ...prev, title: editTitle, note: editNote }));
      setTitle(editTitle);
      setNote(editNote);
      setEditingTitle(false);
      message.success('已儲存');
    } catch (e) {
      message.error('儲存失敗');
    }
  };

  const specWordCount = currentSpec?.spec_content
    ? currentSpec.spec_content.split(/\s+/).length
    : 0;

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#1a1a2e' }}>
            <FileTextOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            開發規格產生器
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            勾選平台功能模組 → AI 生成詳細開發規格 → 複製給 Claude Code 開發
          </Text>
        </div>
        <Space>
          <Button icon={<HistoryOutlined />} onClick={openHistory}>
            版本紀錄
          </Button>
        </Space>
      </div>

      <Tabs defaultActiveKey="spec" style={{ marginBottom: 8 }}>
        <TabPane tab={<span><FileTextOutlined /> 規格產生器</span>} key="spec">

      <Row gutter={20}>
        {/* Left: Feature Selector */}
        <Col span={10}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>功能模組選擇</span>
                <Space size={6}>
                  <Badge count={selected.length} color="#1890ff" showZero />
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  >
                    全選
                  </Checkbox>
                </Space>
              </div>
            }
            style={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}
            styles={{ body: { flex: 1, overflow: 'auto', padding: '8px 16px' } }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (
              Object.entries(catalog).map(([category, features]) => {
                const catKeys = features.map(f => f.key);
                const catAllSelected = catKeys.every(k => selected.includes(k));
                const catSomeSelected = catKeys.some(k => selected.includes(k)) && !catAllSelected;
                return (
                  <div key={category} style={{ marginBottom: 16 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      marginBottom: 6, cursor: 'pointer',
                      padding: '4px 6px', borderRadius: 6,
                      background: '#f5f7ff',
                    }} onClick={() => toggleCategory(features)}>
                      <Checkbox
                        checked={catAllSelected}
                        indeterminate={catSomeSelected}
                        onChange={() => toggleCategory(features)}
                        onClick={e => e.stopPropagation()}
                      />
                      <Tag color={CATEGORY_COLORS[category] || 'default'} style={{ margin: 0, fontWeight: 600 }}>
                        {category}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {catKeys.filter(k => selected.includes(k)).length}/{catKeys.length}
                      </Text>
                    </div>
                    <div style={{ paddingLeft: 16 }}>
                      {features.map(f => (
                        <div key={f.key} style={{ marginBottom: 4 }}>
                          <Checkbox
                            checked={selected.includes(f.key)}
                            onChange={() => toggleFeature(f.key)}
                          >
                            <Tooltip title={f.desc} placement="right" mouseEnterDelay={0.5}>
                              <span style={{ fontSize: 13 }}>{f.label}</span>
                            </Tooltip>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </Card>

          {/* Config panel */}
          <Card size="small" style={{ marginTop: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ fontSize: 12 }}>規格標題</Text>
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="例：Marcom AI Phase 2 開發規格"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <Text strong style={{ fontSize: 12 }}>備註 / 版本說明</Text>
                <Input
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="例：聚焦 AI 業務競賽模組"
                  style={{ marginTop: 4 }}
                />
              </div>
              <Button
                block
                size="small"
                icon={<EditOutlined />}
                onClick={() => setCtxModalOpen(true)}
              >
                {platformCtx ? '已填寫額外背景說明' : '新增額外背景說明（選填）'}
              </Button>
              <Button
                type="primary"
                block
                size="large"
                icon={generating ? <SyncOutlined spin /> : <RobotOutlined />}
                loading={generating}
                disabled={selected.length === 0}
                onClick={handleGenerate}
                style={{ background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)', border: 'none' }}
              >
                {generating ? 'Claude 正在生成規格...' : `生成開發規格（${selected.length} 個模組）`}
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Right: Spec Viewer */}
        <Col span={14}>
          <Card
            title={
              currentSpec ? (
                editingTitle ? (
                  <Space>
                    <Input
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      style={{ width: 280 }}
                    />
                    <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveTitle}>儲存</Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingTitle(false)}>取消</Button>
                  </Space>
                ) : (
                  <Space>
                    <span style={{ fontWeight: 700 }}>{currentSpec.title}</span>
                    <Tag color="blue">v{currentSpec.version_num}</Tag>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => { setEditTitle(currentSpec.title); setEditNote(currentSpec.note || ''); setEditingTitle(true); }}
                    />
                  </Space>
                )
              ) : '開發規格文件'
            }
            extra={
              currentSpec && (
                <Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>約 {specWordCount.toLocaleString()} 字</Text>
                  <Button size="small" icon={<LinkOutlined />} onClick={() => handleCopyLink(currentSpec.share_token)}>複製連結</Button>
                  <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>複製 MD</Button>
                  <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>下載 .md</Button>
                </Space>
              )
            }
            style={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}
            styles={{ body: { flex: 1, overflow: 'auto', padding: '20px 24px' } }}
          >
            {generating ? (
              <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
                <div style={{ marginTop: 20, color: '#595959' }}>
                  <RobotOutlined style={{ fontSize: 32, color: '#722ed1', marginBottom: 12, display: 'block' }} />
                  Claude 正在分析 {selected.length} 個功能模組並生成詳細規格...
                  <br /><Text type="secondary" style={{ fontSize: 12 }}>通常需要 30-60 秒，請耐心等候</Text>
                </div>
              </div>
            ) : currentSpec ? (
              <div>
                {currentSpec.note && (
                  <Alert
                    message={<><Text strong>備註：</Text>{currentSpec.note}</>}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    closable
                  />
                )}
                <div style={{
                  background: '#f8f9ff',
                  border: '1px solid #e6edff',
                  borderRadius: 8,
                  padding: '8px 16px',
                  marginBottom: 16,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                }}>
                  {(currentSpec.selected_features || []).map(key => {
                    const allFeatures = Object.values(catalog).flat();
                    const f = allFeatures.find(x => x.key === key);
                    return f ? (
                      <Tag key={key} color={CATEGORY_COLORS[
                        Object.entries(catalog).find(([, feats]) => feats.some(x => x.key === key))?.[0]
                      ] || 'default'} style={{ fontSize: 11 }}>
                        {f.label}
                      </Tag>
                    ) : null;
                  })}
                </div>
                <SpecMarkdown content={currentSpec.spec_content} />
              </div>
            ) : (
              <Empty
                image={<FileTextOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                description={
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, color: '#595959', marginBottom: 8 }}>尚未生成規格文件</div>
                    <Text type="secondary">在左側勾選需要的功能模組，然後點擊「生成開發規格」</Text>
                    <div style={{ marginTop: 16 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Claude 會根據你選擇的模組生成包含：<br />
                        DB Schema、API 設計、前端元件、整合關係、實作順序 的完整規格文件
                      </Text>
                    </div>
                  </div>
                }
                style={{ padding: '60px 0' }}
              />
            )}
          </Card>
        </Col>
      </Row>

        </TabPane>
        <TabPane tab={<span><ApiOutlined /> API 文件庫</span>} key="apidocs">
          <Card style={{ borderRadius: 10 }}>
            <ApiDocLibrary />
          </Card>
        </TabPane>
      </Tabs>

      {/* History Drawer */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            版本紀錄
            <Badge count={historyList.length} color="#1890ff" />
          </Space>
        }
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        width={480}
        styles={{ body: { padding: 0 } }}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : historyList.length === 0 ? (
          <Empty description="尚無版本紀錄" style={{ padding: 40 }} />
        ) : (
          <List
            dataSource={historyList}
            renderItem={spec => (
              <List.Item
                style={{ padding: '12px 20px', cursor: 'pointer' }}
                onClick={() => loadFromHistory(spec.id)}
                actions={[
                  <Tooltip key="link" title="複製閱讀連結（無需登入）">
                    <Button
                      size="small"
                      type="text"
                      icon={<LinkOutlined />}
                      onClick={e => { e.stopPropagation(); handleCopyLink(spec.share_token); }}
                    />
                  </Tooltip>,
                  <Popconfirm
                    key="del"
                    title="確定刪除此版本？"
                    onConfirm={e => deleteFromHistory(spec.id, e)}
                    okText="刪除"
                    cancelText="取消"
                    okType="danger"
                  >
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={e => e.stopPropagation()}
                    />
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 40, height: 40, borderRadius: 8,
                      background: 'linear-gradient(135deg, #1890ff, #722ed1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: 14,
                    }}>
                      v{spec.version_num}
                    </div>
                  }
                  title={
                    <Space size={4}>
                      <span style={{ fontWeight: 600 }}>{spec.title}</span>
                      {currentSpec?.id === spec.id && <Tag color="blue" style={{ fontSize: 10 }}>當前</Tag>}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {spec.feature_count} 個模組 ·{' '}
                        {spec.creator_name && `${spec.creator_name} · `}
                        {spec.created_at ? new Date(spec.created_at).toLocaleString('zh-TW') : ''}
                      </div>
                      {spec.note && (
                        <div style={{ fontSize: 11, color: '#595959', marginTop: 2 }}>
                          {spec.note}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      {/* Platform Context Modal */}

      <Modal
        title="額外背景說明"
        open={ctxModalOpen}
        onOk={() => setCtxModalOpen(false)}
        onCancel={() => setCtxModalOpen(false)}
        okText="確認"
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          可以在這裡補充給 Claude 的額外說明，例如：目標開發平台的差異、特殊限制、優先功能、技術棧差異等。這些說明會加入生成提示中。
        </Paragraph>
        <TextArea
          value={platformCtx}
          onChange={e => setPlatformCtx(e.target.value)}
          rows={8}
          placeholder="例：目標平台使用 Nuxt.js 而非 React。資料庫使用 MySQL。需要支援多租戶架構，每個客戶有獨立 schema。優先實作 AI 業務競賽和影片分析兩個模組..."
        />
      </Modal>
    </div>
  );
}
